import gql from 'graphql-tag';
import apolloClient from '../apollo';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

/**
 * State
 */
const getDefaultState = () => {
  return {
    gps: [],
    gpsCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for a GPS entry
 * used for all queries/mutations which return a GPS entry
 * @type {DocumentNode}
 */
const GPS_REQUEST_FIELDS = gql`
  fragment gpsFields on Gps {
    id
    number
    user {
      id
      firstname
      lastname
      email
    }
    timeStamp
    createdAt
    updatedAt
    long
    lat
  }
`;

/**
 * Actions
 */
const actions = {
  async fetchGps({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query gpsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: GpsFilterInput
          ) {
            gpsPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              gpss {
                ...gpsFields
              }
              gpsCount
            }
          }
          ${GPS_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });

      console.log('response.data.gpsPaginated.gpss', response.data.gpsPaginated.gpss);

      commit('SET_GPS', response.data.gpsPaginated.gpss);
      commit('SET_GPS_COUNT', response.data.gpsPaginated.gpsCount);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createGps({ commit }, gpsInput) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createGps($gpsInput: GpsInput!) {
            createGps(gpsInput: $gpsInput) {
              ...gpsFields
            }
          }
          ${GPS_REQUEST_FIELDS}
        `,
        variables: {
          gpsInput: gpsInput,
        },
      });

      commit('ADD_GPS', response.data.createGps);
      return response.data.createGps;
    } catch (err) {
      throw new Error(err);
    }
  },

  async updateGps({ commit }, { gpsNumber, gpsInput }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateGps($gpsNumber: Int!, $gpsInput: GpsInput!) {
            updateGps(gpsNumber: $gpsNumber, gpsInput: $gpsInput) {
              ...gpsFields
            }
          }
          ${GPS_REQUEST_FIELDS}
        `,
        variables: {
          gpsNumber: gpsNumber,
          gpsInput: gpsInput,
        },
      });

      commit('UPDATE_GPS', response.data.updateGps);
      return response.data.updateGps;
    } catch (err) {
      throw new Error(err);
    }
  },

  async deleteGps({ commit }, gpsNumber) {
    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation deleteGps($gpsNumber: Int!) {
            deleteGps(gpsNumber: $gpsNumber)
          }
        `,
        variables: {
          gpsNumber: gpsNumber,
        },
      });

      commit('DELETE_GPS', gpsNumber);
      return true;
    } catch (err) {
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_GPS: (state, gps) => (gps ? (state.gps = gps) : (state.gps = [])),
  ADD_GPS: (state, gps) => state.gps.push(gps),
  UPDATE_GPS: (state, updatedGps) => {
    const index = state.gps.findIndex((gps) => gps.number === updatedGps.number);
    if (index !== -1) {
      state.gps.splice(index, 1, updatedGps);
    }
  },
  DELETE_GPS: (state, gpsNumber) => {
    state.gps = state.gps.filter((gps) => gps.number !== gpsNumber);
  },
  SET_GPS_COUNT: (state, gpsCount) => (state.gpsCount = gpsCount),
};

/**
 * Getters
 */
const getters = {
  getGps: (state) => {
    return state.gps;
  },
  getGpsById: (state) => (gpsId) => state.gps.find((gps) => gps.id === gpsId),
  getGpsByNumber: (state) => (gpsNumber) => state.gps.find((gps) => gps.number === gpsNumber),
  getGpsCount: (state) => state.gpsCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
